import moment from "moment";

/**
 * Pre-shipment test: the pure parts of the admin page that checks whether a
 * batch of devices has ever been used before it goes out to a customer. The
 * server (POST /devices/usage) does the actual checks; this module mirrors its
 * input rules so the page can validate before it calls, and turns each result
 * into something a table row or a spreadsheet cell can show.
 */

/** Same cap as checkDeviceUsage.MAX_SERIALS on the server. */
export const MAX_SERIALS = 500;

/**
 * Same splitting rule as the server: commas, semicolons and any whitespace all
 * separate serials, so a column pasted from a spreadsheet works as well as a
 * comma list. Blanks are dropped; duplicates are kept so the count shown
 * matches what was typed (the server checks each serial once).
 */
export function splitSerials(text) {
  if (!text) return [];
  return text
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Adds a serial to the box on its own line, unless it is already in there in
 * any of the accepted forms. The operator's own formatting is left alone.
 */
export function addSerial(text, serial) {
  const current = text || "";
  if (splitSerials(current).includes(serial)) return current;
  const trimmed = current.replace(/\s+$/, "");
  return trimmed ? `${trimmed}\n${serial}` : serial;
}

/**
 * Takes a serial out of the box. The remaining serials are rewritten one per
 * line: a mid-line removal from "1, 2, 3" has no obviously right spelling, and
 * one per line is what the picker writes anyway.
 */
export function removeSerial(text, serial) {
  const entries = splitSerials(text);
  if (!entries.includes(serial)) return text || "";
  return entries.filter((s) => s !== serial).join("\n");
}

export const RESULT_READY = "ready";
export const RESULT_USED = "used";
export const RESULT_NOT_FOUND = "not_found";
export const RESULT_INVALID = "invalid";

/**
 * One verdict per row. "Ready to ship" is the only green outcome: the device
 * exists and none of the four usage checks fired. Anything the server could
 * not check (not a serial, or no such device) gets its own state rather than
 * being lumped in with used, because both need a human to look at the label.
 */
export function classifyResult(row) {
  if (!row) return RESULT_INVALID;
  if (row.serial_number == null) return RESULT_INVALID;
  if (!row.found) return RESULT_NOT_FOUND;
  return row.used ? RESULT_USED : RESULT_READY;
}

export const RESULT_LABELS = {
  [RESULT_READY]: "Ready to ship",
  [RESULT_USED]: "Used",
  [RESULT_NOT_FOUND]: "Not found",
  [RESULT_INVALID]: "Invalid serial",
};

/**
 * The bands the device status uses for its own alarms, so a battery the
 * portal would flag as low is flagged the same way here.
 */
export const BATTERY_LOW_PERCENT = 10;
export const BATTERY_CRITICAL_PERCENT = 5;

export function batteryTone(percent) {
  if (percent == null) return null;
  if (percent <= BATTERY_CRITICAL_PERCENT) return "critical";
  if (percent <= BATTERY_LOW_PERCENT) return "low";
  return "ok";
}

/** Raw readings are millivolts; people read volts. */
export function formatVolts(millivolts) {
  if (millivolts == null) return null;
  return `${(millivolts / 1000).toFixed(2)} V`;
}

export const BATTERY_SOURCE_READING = "reading";
export const BATTERY_SOURCE_RECORD = "record";

/**
 * The battery level to show and where it came from. A real reading (the
 * telemetry-derived percentage) always wins. Otherwise the value stored on the
 * device row is shown as "last known" - it outlives trimmed telemetry - but
 * only once the device has actually been in service: on a unit that was never
 * activated and never reported, that stored value is the placeholder 100 that
 * provisioning writes, not a measurement, so nothing is shown.
 */
export function batteryDisplay(row) {
  if (!row || !row.found) return null;

  if (row.battery_percent != null) {
    return {
      source: BATTERY_SOURCE_READING,
      percent: row.battery_percent,
      reading: row.battery_reading,
      time: row.battery_reading_time,
    };
  }

  const inService = row.activated || row.ever_installed_on_asset || row.has_telemetry;
  if (row.device_battery_percent != null && inService) {
    return {
      source: BATTERY_SOURCE_RECORD,
      percent: row.device_battery_percent,
      reading: null,
      time: row.status_updated_time,
    };
  }

  return null;
}

const DATE_FORMAT = "MMM D, YYYY";
const DATE_TIME_FORMAT = "MMM D, YYYY HH:mm";

export function formatDate(value) {
  return value ? moment.utc(value).format(DATE_FORMAT) : "";
}

export function formatDateTime(value) {
  return value ? moment.utc(value).format(DATE_TIME_FORMAT) + " UTC" : "";
}

/** "Active, expires Mar 4, 2027" / "Removed (2)" / "None". */
export function licenseSummary(row) {
  if (!row || !row.has_license) return "None";
  if (row.current_license_id == null) {
    return row.license_count > 1
      ? `Removed (${row.license_count})`
      : "Removed";
  }
  const state = row.license_active ? "Active" : "Inactive";
  const expiry = formatDate(row.license_expiry_date);
  return expiry ? `${state}, expires ${expiry}` : state;
}

/** "Asset name" / "Previously installed" / "Never". */
export function assetSummary(row) {
  if (!row) return "";
  if (row.installed_on_asset) {
    return row.asset_name || `Asset ${row.asset_id}`;
  }
  return row.ever_installed_on_asset ? "Previously installed" : "Never";
}

/** "Mar 4, 2026" / "Yes" / "No". */
export function activationSummary(row) {
  if (!row || !row.activated) return "No";
  return formatDate(row.activation_time) || "Yes";
}

/** "Last seen Mar 4, 2026 05:06 UTC" / "Yes" / "None". */
/** "Last seen Mar 4, 2026 05:06 UTC" / "Bench test, last seen …" / "Yes" / "None". */
export function telemetrySummary(row) {
  if (!row || !row.has_telemetry) return "None";
  const seen = formatDateTime(row.last_telemetry_time);
  if (row.bench_telemetry) return seen ? `Bench test, last seen ${seen}` : "Bench test";
  return seen ? `Last seen ${seen}` : "Yes";
}

/**
 * "Traffix stock" while the unit has never belonged to anyone else; otherwise
 * the customer it belongs to. Leaving stock is one of the four ownership marks.
 */
export function customerSummary(row) {
  if (!row || !row.found) return "";
  if (row.in_stock) return "Traffix stock";
  return row.customer_name || (row.customer_id != null ? `Customer ${row.customer_id}` : "Left stock");
}

/**
 * The spreadsheet columns, in order. Each entry knows how to read its value
 * off a result row so the table on screen and the file on disk cannot drift
 * apart. Values are kept typed (numbers, booleans) where a cell benefits from
 * it; dates are formatted as text so they read the same in every locale.
 */
export const EXPORT_COLUMNS = [
  { header: "Serial", width: 16, value: (r) => r.serial },
  { header: "Result", width: 16, value: (r) => RESULT_LABELS[classifyResult(r)] },
  { header: "Device ID", width: 11, value: (r) => r.device_id },
  { header: "Device name", width: 22, value: (r) => r.device_name },
  { header: "Device type", width: 18, value: (r, ctx) => ctx.deviceTypeLabel(r.devicetype_id) },
  { header: "Device status", width: 18, value: (r) => r.device_status },
  { header: "Active", width: 9, value: (r) => (r.found ? yesNo(r.device_active) : null) },
  { header: "Deleted", width: 9, value: (r) => (r.found ? yesNo(r.device_deleted) : null) },
  { header: "Damaged", width: 10, value: (r) => (r.found ? yesNo(r.damaged) : null) },
  { header: "Customer", width: 24, value: (r) => r.customer_name },
  { header: "Tenant ID", width: 10, value: (r) => r.tenant_id },
  { header: "Battery %", width: 11, value: (r) => r.battery_percent },
  { header: "Battery (V)", width: 12, value: (r) => (r.battery_reading != null ? r.battery_reading / 1000 : null) },
  { header: "Battery measured", width: 22, value: (r) => formatDateTime(r.battery_reading_time) },
  { header: "Last known battery %", width: 20, value: (r) => r.device_battery_percent },
  { header: "Signal %", width: 10, value: (r) => r.signal_percent },
  { header: "Temperature (F)", width: 15, value: (r) => r.temperature },
  { header: "Firmware", width: 12, value: (r) => r.firmware },
  { header: "ICCID", width: 24, value: (r) => r.iccid },
  { header: "Last check-in", width: 22, value: (r) => formatDateTime(r.status_updated_time) },
  { header: "In stock", width: 10, value: (r) => (r.found ? yesNo(r.in_stock) : null) },
  { header: "Telemetry", width: 13, value: (r) => (r.found ? (r.bench_telemetry ? "Yes (bench)" : yesNo(r.has_telemetry)) : null) },
  { header: "Last telemetry", width: 22, value: (r) => formatDateTime(r.last_telemetry_time) },
  { header: "License", width: 28, value: (r) => (r.found ? licenseSummary(r) : null) },
  { header: "License count", width: 13, value: (r) => (r.found ? r.license_count : null) },
  { header: "Installed on asset", width: 24, value: (r) => (r.found ? assetSummary(r) : null) },
  { header: "First installed", width: 22, value: (r) => formatDateTime(r.customer_activation_time) },
  { header: "Activated", width: 14, value: (r) => (r.found ? activationSummary(r) : null) },
  { header: "Hologram registered", width: 20, value: (r) => (r.found ? hologramRegisteredSummary(r) : null) },
  { header: "Hologram state", width: 16, value: (r) => r.hologram_state },
  { header: "Hologram claimed", width: 22, value: (r) => formatDateTime(r.hologram_claimed_time) },
  { header: "Hologram connected", width: 20, value: (r) => (r.found ? hologramConnectedSummary(r) : null) },
  { header: "Hologram last connect", width: 22, value: (r) => formatDateTime(r.hologram_last_connect_time) },
  { header: "Hologram network", width: 22, value: (r) => r.hologram_last_network },
  { header: "Hologram data (MB)", width: 18, value: (r) => (r.hologram_data_used_bytes != null ? Math.round(r.hologram_data_used_bytes / 10485.76) / 100 : null) },
  { header: "Hologram IMEI", width: 18, value: (r) => r.hologram_imei },
  { header: "Details", width: 60, value: (r) => (r.reasons || []).join(" ") },
  { header: "Error", width: 32, value: (r) => r.error },
];

/** "Yes, LIVE" / "No" / "Unknown" (the carrier could not be asked). */
export function hologramRegisteredSummary(row) {
  if (!row) return "";
  if (!row.hologram_checked) return "Unknown";
  if (!row.hologram_registered) return "No";
  return row.hologram_state ? `Yes, ${row.hologram_state}` : "Yes";
}

/** "Last Sep 17, 2026 05:35 UTC" / "Yes" / "Never" / "Unknown". */
export function hologramConnectedSummary(row) {
  if (!row) return "";
  if (!row.hologram_checked) return "Unknown";
  if (!row.hologram_connected) return "Never";
  const last = formatDateTime(row.hologram_last_connect_time);
  /* A session from a unit still in stock is the bench at Traffix, like its telemetry. */
  if (row.in_stock) return last ? `Bench test, last ${last}` : "Bench test";
  return last ? `Last ${last}` : "Yes";
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

/**
 * One plain object per result row, keyed by column header, ready for a
 * spreadsheet writer. `ctx.deviceTypeLabel` resolves a type id to its name.
 */
export function toExportRows(results, ctx) {
  return (results || []).map((row) => {
    const out = {};
    EXPORT_COLUMNS.forEach((column) => {
      const value = column.value(row, ctx);
      out[column.header] = value === undefined ? null : value;
    });
    return out;
  });
}

/** File name with the check date in it, e.g. pre-shipment-test_2026-09-17.xlsx */
export function exportFileName(date = new Date()) {
  return `pre-shipment-test_${moment(date).format("YYYY-MM-DD")}.xlsx`;
}
